import { cn } from "@gymflow/helpers";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";

import {
  Button,
  ChevronDownIcon,
  LayeredCircles,
} from "../../components/atoms";
import { RocketIcon } from "../../components/atoms/icons/RocketIcon";

export const OnboardingBanner = ({
  currentScreen,
  setCurrentScreen,
}: {
  currentScreen: "none" | "banner" | "form";
  setCurrentScreen: (currentScreen: "none" | "banner" | "form") => void;
}) => {
  const { t } = useTranslation();
  return (
    <div className="relative flex w-full">
      <AnimatePresence>
        <motion.div
          className={cn(
            "track-height relative flex items-center justify-between w-full gap-4 dark:border-secondary-800 border-secondary-200 bg-secondary-25 dark:bg-secondary-950 z-10 border-b transition px-4 py-3 lg:px-28",
            {
              "py-6": currentScreen === "form",
              "flex-col": currentScreen !== "banner",
              "flex-row": currentScreen === "banner",
            },
          )}
          layout
          initial={{ height: "auto" }}
          animate={{ height: currentScreen === "form" ? "12rem" : "4.5rem" }}
          exit={{ height: "auto" }}
        >
          <motion.div layout>
            <LayeredCircles className="size-10 transition" intent="secondary">
              <RocketIcon className="stroke-secondary-700 dark:stroke-secondary-300 size-5" />
            </LayeredCircles>
          </motion.div>
          {currentScreen === "banner" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, transition: { delay: 0.2 } }}
              exit={{ opacity: 0 }}
              className="mr-8 flex-1 text-sm font-medium text-gray-500"
              layout
            >
              {t("pages.onboarding.banner.description")}
            </motion.div>
          )}
          {currentScreen === "form" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, transition: { delay: 0.2 } }}
              exit={{ opacity: 0 }}
              layout
              className="flex flex-col items-center gap-2"
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, transition: { delay: 0.2 } }}
                exit={{ opacity: 0 }}
                className="text-secondary-700 dark:text-secondary-200 text-xl font-bold"
              >
                {t("pages.onboarding.banner.title")}
              </motion.div>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, transition: { delay: 0.4 } }}
                exit={{ opacity: 0 }}
                className="whitespace-pre-line text-center text-sm font-medium text-gray-500"
              >
                {t("pages.onboarding.form.description")}
              </motion.div>
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>
      <AnimatePresence mode="wait">
        {currentScreen === "form" ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute right-4 top-4 z-20"
          >
            <Button
              className="size-10"
              size="icon"
              intent="secondary-outline"
              onClick={() => setCurrentScreen("banner")}
            >
              <XMarkIcon className="text-secondary-700 size-5" />
            </Button>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute right-4 top-4 z-20"
          >
            <Button
              className="size-10"
              intent="transparent"
              size="icon"
              onClick={() => setCurrentScreen("form")}
            >
              <ChevronDownIcon className="text-secondary-700 size-5" />
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
